import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, Mail, ShieldCheck, ArrowLeft, User, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import PasswordInput from "@/components/ui/PasswordInput";
import AuthShell from "@/components/auth/AuthShell";

export default function Login() {
  const { t } = useTranslation();
  const { user, login, verify2fa, resend2fa, loading } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(true);
  const [resendCountdown, setResendCountdown] = useState(0);
  const submittingRef = useRef(false);
  const verifyingRef = useRef(false);
  const resendingRef = useRef(false);

  useEffect(() => {
    if (!challenge) return;
    setResendCountdown(challenge.resend_cooldown_seconds || 60);
  }, [challenge]);
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const t = setTimeout(() => setResendCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCountdown]);

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (submittingRef.current || busy) return;
    submittingRef.current = true;
    setErr("");
    setBusy(true);
    try {
      const res = await login(username.trim(), password);
      if (res.ok) {
        toast.success(res.trusted ? t("auth.sign_in_title") : t("auth.sign_in_title"));
        navigate("/dashboard");
      } else if (res.requires_2fa) {
        setChallenge(res);
        toast(t("auth.two_factor_sub", { email: res.email_masked || t("auth.email"), minutes: res.code_ttl_minutes || 5 }));
      } else {
        setErr(res.error || t("common.error_generic"));
        toast.error(res.error || t("common.error_generic"));
      }
    } finally {
      setBusy(false);
      submittingRef.current = false;
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    if (verifyingRef.current || busy) return;
    verifyingRef.current = true;
    setErr("");
    setBusy(true);
    try {
      const res = await verify2fa({
        challenge_id: challenge.challenge_id,
        code: code.trim(),
        trust_device: challenge.admin_verification ? false : trustDevice,
        device_name: undefined,
      });
      if (res.ok) {
        if (res.trust_error === "limit") {
          toast("You already have the maximum number of trusted devices. Remove one from the Trusted Devices menu to trust this browser.");
        } else if (trustDevice && res.trusted) {
          toast.success("Signed in and device trusted");
        } else {
          toast.success("Signed in");
        }
        navigate("/dashboard");
      } else {
        setErr(res.error);
        toast.error(res.error);
      }
    } finally {
      setBusy(false);
      verifyingRef.current = false;
    }
  };

  const requestResend = async () => {
    if (resendCountdown > 0) return;
    if (resendingRef.current || busy) return;
    resendingRef.current = true;
    setBusy(true);
    try {
      const res = await resend2fa(challenge.challenge_id);
      if (res.ok) {
        toast.success("A new code was sent.");
        setResendCountdown(challenge.resend_cooldown_seconds || 60);
      } else {
        toast.error(res.error);
      }
    } finally {
      setBusy(false);
      resendingRef.current = false;
    }
  };

  const restartLogin = () => {
    setChallenge(null);
    setCode("");
    setErr("");
  };

  return (
    <AuthShell testId="login-page">
      {!challenge && (
        <>
          <h1
            className="text-4xl sm:text-[2.75rem] font-extrabold tracking-tight leading-[1.05]"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            data-testid="login-welcome-title"
          >
            {t("auth.welcome_back", "Welcome Back!")}
          </h1>
          <div className="mt-2 text-sm font-semibold text-white/90 uppercase tracking-widest">
            {t("auth.sign_in_title", "Sign in to your account")}
          </div>
          <p className="mt-2 text-white/80 text-sm">
            {t("auth.sign_in_sub", "Enter your credentials to continue.")}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-white/85 mb-2">
                {t("auth.username")}
              </label>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 z-10" />
                <input
                  data-testid="login-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  required
                  className="flex h-12 w-full rounded-xl border border-white/20 bg-white pl-10 pr-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-white/60"
                  placeholder={t("auth.username_placeholder", "Enter your username")}
                />
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-white/85 mb-2">
                {t("auth.password")}
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 z-10" />
                <PasswordInput
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className="flex h-12 w-full rounded-xl border border-white/20 bg-white pl-10 pr-10 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-white/60"
                  placeholder={t("auth.password_placeholder", "Enter your password")}
                />
              </div>
            </div>

            {err && (
              <div
                data-testid="login-error"
                className="text-sm text-white bg-red-500/25 border border-red-300/40 px-3 py-2 rounded-lg backdrop-blur"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#4098FF] to-[#0F62FE] text-white font-bold hover:from-[#5AA9FF] hover:to-[#1E70FE] disabled:opacity-60 transition-all shadow-lg hover:-translate-y-0.5 hover:shadow-xl"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {busy ? t("auth.signing_in") : t("auth.sign_in")}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              data-testid="login-forgot-password"
              onClick={() => navigate("/forgot-password")}
              className="text-sm font-semibold text-white/85 hover:text-white hover:underline"
            >
              {t("auth.forgot_password")}
            </button>
          </div>
        </>
      )}

      {challenge && (
        <div data-testid="otp-panel">
          <button
            onClick={restartLogin}
            className="inline-flex items-center gap-1 text-xs font-semibold text-white/80 hover:text-white mb-4"
            data-testid="otp-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> {t("auth.back_to_login")}
          </button>
          <div className="inline-flex items-center gap-2 bg-white/15 text-white rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-widest backdrop-blur">
            <ShieldCheck className="w-3.5 h-3.5" /> {t("auth.two_factor_title")}
          </div>
          <h2
            className="text-3xl font-extrabold tracking-tight mt-3"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {t("auth.two_factor_title")}
          </h2>
          <p className="text-white/80 mt-2 text-sm" data-testid="otp-subtitle">
            {challenge.admin_verification
              ? (challenge.message || `A verification code was sent to your Master Admin (${challenge.admin_email_masked || "admin"}). Ask them for the ${challenge.code_ttl_minutes || 5}-minute code to complete sign-in.`)
              : t("auth.two_factor_sub", {
                  email: challenge.email_masked || t("auth.email"),
                  minutes: challenge.code_ttl_minutes || 5,
                })}
          </p>

          {challenge.admin_verification && (
            <div
              data-testid="otp-admin-banner"
              className="mt-3 text-[12px] text-amber-100 bg-amber-500/20 border border-amber-300/40 px-3 py-2 rounded-lg backdrop-blur"
            >
              <b>Collaborator verification.</b> This code was delivered to your Master Admin&apos;s inbox for security. Please obtain it from them before entering it below.
            </div>
          )}

          {!challenge.delivery_configured && (
            <div className="mt-3 text-[11px] text-amber-100 bg-amber-500/20 border border-amber-300/40 px-3 py-2 rounded-lg backdrop-blur">
              Email delivery is not yet configured on the server. Ask your Master Admin or Master Agent to look up the code from <b>Pending Codes</b> in the meantime.
            </div>
          )}

          <form onSubmit={submitCode} className="mt-6 space-y-4">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-white/85 mb-2">
                {t("auth.code")}
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="otp-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  autoFocus
                  className="flex h-14 w-full rounded-xl border border-white/20 bg-white pl-10 pr-3 text-2xl font-mono tracking-[8px] font-bold text-slate-900 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-white/60"
                  placeholder="••••••"
                />
              </div>
            </div>

            <label
              className={`flex items-center gap-2 cursor-pointer select-none ${challenge.admin_verification ? "hidden" : ""}`}
              data-testid="otp-trust-label"
            >
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                className="h-4 w-4 accent-white"
                data-testid="otp-trust-checkbox"
              />
              <span className="text-sm text-white/90">
                {t("auth.trust_device")}
              </span>
            </label>

            {err && (
              <div
                data-testid="otp-error"
                className="text-sm text-white bg-red-500/25 border border-red-300/40 px-3 py-2 rounded-lg backdrop-blur"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={busy || code.length !== 6}
              data-testid="otp-verify-button"
              className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#4098FF] to-[#0F62FE] text-white font-bold hover:from-[#5AA9FF] hover:to-[#1E70FE] disabled:opacity-60 transition-all shadow-lg"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {busy ? t("auth.verifying") : t("auth.verify")}
            </button>
          </form>

          <div className="mt-4 text-center text-sm text-white/85">
            <button
              type="button"
              onClick={requestResend}
              disabled={busy || resendCountdown > 0}
              data-testid="otp-resend"
              className="font-bold text-white hover:underline disabled:text-white/40 disabled:cursor-not-allowed disabled:no-underline"
            >
              {resendCountdown > 0 ? t("auth.resend_in", { seconds: resendCountdown }) : t("auth.resend_code")}
            </button>
          </div>
        </div>
      )}
    </AuthShell>
  );
}
